import { bandRole, playersAbove } from "../document/band.ts";
import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import { COMPILED_COLLISION_CELLS_PER_TILE, type MapDocument, type TerrainLayer, type TileLayer, type WorldDocument } from "../document/types.ts";
import type { CompileContext, SurfaceRules } from "./context.ts";
import { COMPILED_MIN_COVER, stampPassage } from "./footprint.ts";
import { passageFootprint } from "./prop-footprint.ts";

/**
 * Ground surfaces in the client chunk: what each tile of a map is made of, as the game names it, so a client can ask
 * what a character stands on. The names are the game's and Daedalus reads nothing into them: the compile only folds
 * the layers drawn under every character, bottom to top, and writes the name of the topmost layer that names a cell.
 * Those are every terrain layer, which is drawn flat even in the Players band (ADR-047's amendment of 2026-10-03),
 * and the tile layers under the band; a tile layer in the band sorts with characters (a fence, a roof) and names
 * nothing. A terrain layer names a cell whose four corners it paints; a tile layer names a cell by the tileset of
 * the tile on it. A layer the rules do not name leaves what lies under it.
 *
 * Over the ground, a placed prop whose footprint has a passage and names a surface gives that surface to every tile
 * its passage lays a cell in (ADR-017's amendment of 2026-10-05, decision 8), so a character on a bridge's deck is
 * told the deck and not the water under it.
 */

/**
 * The most names one map or chunk may hold: a cell is one byte and 0 is no surface.
 */
export const MAX_SURFACES = 255;

/**
 * A map's surfaces: one byte per tile, each 0 or a 1-based index into `names`, in the order the fold first met them.
 */
export interface MapSurfaces {
  readonly names: readonly string[];
  readonly cells: Uint8Array;
}

/**
 * One client chunk's surfaces: `chunkSize * chunkSize` bytes, each 0 or a 1-based index into `names`, the names in
 * the order the chunk first meets them.
 */
export interface ChunkSurfaces {
  readonly names: readonly string[];
  readonly cells: Uint8Array;
}

/**
 * A surface name's index in a map's names, added when new.
 *
 * @param names The names met so far.
 * @param name The name.
 * @returns The 1-based index.
 * @throws Error when the map would hold more than `MAX_SURFACES` names.
 */
function indexOfName(names: string[], name: string): number {
  const at = names.indexOf(name);
  if (at !== -1) {
    return at + 1;
  }
  if (names.length === MAX_SURFACES) {
    throw new Error(`the game names more than ${MAX_SURFACES} surfaces, more than a cell's byte holds`);
  }
  names.push(name);
  return names.length;
}

/**
 * Writes a terrain layer's surface over the cells whose four corners it paints. The name joins the map's names with
 * the first cell it takes.
 *
 * @param layer The terrain layer.
 * @param name The surface the game names the layer's terrain.
 * @param names The names met so far.
 * @param width Map width.
 * @param height Map height.
 * @param cells The map's cells.
 */
function foldTerrain(layer: TerrainLayer, name: string, names: string[], width: number, height: number, cells: Uint8Array): void {
  const stride = width + 1;
  let value = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (layer.corners[y * stride + x] !== 0 && layer.corners[y * stride + x + 1] !== 0 && layer.corners[(y + 1) * stride + x] !== 0 && layer.corners[(y + 1) * stride + x + 1] !== 0) {
        value = value === 0 ? indexOfName(names, name) : value;
        cells[y * width + x] = value;
      }
    }
  }
}

/**
 * Writes a tile layer's surfaces over the cells holding a tile of a tileset the rules name. A name joins the map's
 * names with the first cell it takes.
 *
 * @param layer The tile layer.
 * @param tilesets Each tileset index's surface name, undefined for a tileset the rules do not name.
 * @param names The names met so far.
 * @param cells The map's cells.
 */
function foldTiles(layer: TileLayer, tilesets: readonly (string | undefined)[], names: string[], cells: Uint8Array): void {
  for (let i = 0; i < cells.length; i += 1) {
    const packed = layer.tiles[i] as number;
    if (packed === EMPTY_TILE) {
      continue;
    }
    const name = tilesets[unpackTile(packed).tilesetIndex];
    if (name !== undefined) {
      cells[i] = indexOfName(names, name);
    }
  }
}

/**
 * Writes the surface of every deck over the tiles it lies in: each prop that lays a passage (`passageFootprint`),
 * in layer order and then list order so the topmost names a shared tile, whose footprint names a surface. A tile
 * takes the name when the passage lays at least one collision cell in it, laid as the compile lays it
 * (`stampPassage`). Inert wherever the passage itself is, and a prop with cells of its own names its frame's
 * surface.
 *
 * @param world The world, for its tile size.
 * @param map The map.
 * @param context The compile context, for the rules, the footprints and the frames' sizes.
 * @param names The names met so far.
 * @param cells The map's cells.
 */
function foldDecks(world: WorldDocument, map: MapDocument, context: Pick<CompileContext, "collision" | "frameSizes">, names: string[], cells: Uint8Array): void {
  const per = COMPILED_COLLISION_CELLS_PER_TILE;
  const gridWidth = map.width * per;
  let scratch: Uint8Array | null = null;
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const footprint = passageFootprint(prop, context, world.tileSize);
      if (footprint?.surface === undefined) {
        continue;
      }
      scratch ??= new Uint8Array(gridWidth * map.height * per);
      stampPassage(scratch, map.width, map.height, footprint, prop, world.tileSize, per, 1, COMPILED_MIN_COVER);
      const x0 = Math.max(0, Math.floor(prop.x) - 1);
      const x1 = Math.min(map.width - 1, Math.ceil(prop.x + footprint.w / world.tileSize));
      const y0 = Math.max(0, Math.floor(prop.y + 1 - footprint.h / world.tileSize) - 1);
      const y1 = Math.min(map.height - 1, Math.ceil(prop.y + 1));
      for (let ty = y0; ty <= y1; ty += 1) {
        for (let tx = x0; tx <= x1; tx += 1) {
          let lies = false;
          for (let cy = 0; cy < per; cy += 1) {
            const row = (ty * per + cy) * gridWidth + tx * per;
            for (let cx = 0; cx < per; cx += 1) {
              lies = lies || scratch[row + cx] !== 0;
              scratch[row + cx] = 0;
            }
          }
          if (lies) {
            cells[ty * map.width + tx] = indexOfName(names, footprint.surface);
          }
        }
      }
    }
  }
}

/**
 * A map's surfaces: the layers drawn under every character folded bottom to top, then the decks of the props
 * placed over them.
 *
 * @param world The world, for its tileset table.
 * @param map The map.
 * @param rules The game's surface names by terrain and by tileset, or undefined when it names none.
 * @param context The compile context's collision rules and frame sizes, for the footprints whose passage names a
 *   surface; absent, no prop names one.
 * @returns The surfaces, or null when no cell is named.
 * @throws Error when more than `MAX_SURFACES` names are met.
 */
export function mapSurfaces(world: WorldDocument, map: MapDocument, rules: SurfaceRules | undefined, context?: Pick<CompileContext, "collision" | "frameSizes">): MapSurfaces | null {
  if (rules === undefined) {
    return null;
  }
  const names: string[] = [];
  const cells = new Uint8Array(map.width * map.height);
  const tilesets = world.tilesets.map((tileset) => rules.tilesets[tileset.id]);
  const band = playersAbove(map);
  map.layers.forEach((layer, index) => {
    if (layer.kind === "terrain") {
      const name = rules.terrains[layer.terrain];
      if (name !== undefined) {
        foldTerrain(layer, name, names, map.width, map.height, cells);
      }
    } else if (layer.kind === "tile" && bandRole(layer.kind, index, band) === "ground") {
      foldTiles(layer, tilesets, names, cells);
    }
  });
  if (context !== undefined) {
    foldDecks(world, map, context, names, cells);
  }
  return cells.some((value) => value !== 0) ? { names, cells } : null;
}

/**
 * One chunk's cells of a map's surfaces, renumbered into the chunk's own names.
 *
 * @param surfaces The map's surfaces, or null for none.
 * @param map The map, for its size.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The chunk's surfaces, or null when it holds no named cell.
 */
export function chunkSurfaces(surfaces: MapSurfaces | null, map: MapDocument, cx: number, cy: number, chunkSize: number): ChunkSurfaces | null {
  if (surfaces === null) {
    return null;
  }
  const cells = new Uint8Array(chunkSize * chunkSize);
  const remap = new Uint8Array(surfaces.names.length + 1);
  const names: string[] = [];
  for (let y = 0; y < chunkSize && cy * chunkSize + y < map.height; y += 1) {
    for (let x = 0; x < chunkSize && cx * chunkSize + x < map.width; x += 1) {
      const value = surfaces.cells[(cy * chunkSize + y) * map.width + cx * chunkSize + x] as number;
      if (value === 0) {
        continue;
      }
      if (remap[value] === 0) {
        names.push(surfaces.names[value - 1] as string);
        remap[value] = names.length;
      }
      cells[y * chunkSize + x] = remap[value] as number;
    }
  }
  return names.length === 0 ? null : { names, cells };
}
